import { useEffect, useCallback, useMemo, useRef } from 'react'
import { useHistory, useLocation } from 'react-router'
import { LocationDescriptor, LocationState } from 'history'
import forEach from 'lodash/forEach'
import { qsParse } from 'helpers/qsParse'
import { injectCss } from 'helpers'
import { useGetSize } from './useGetSize'

export const useWindowSize = () => {
  const getSize = useCallback(
    () => ({
      width: window.innerWidth,
      height: window.innerHeight,
    }),
    []
  )

  const size = useGetSize(getSize)

  return size
}

type Parse<K extends string, V = string> = Record<K, V | V[] | undefined>

type ValueParse = Parse<string, string | number | boolean>

type Values<K extends string | ValueParse> = {
  [P in K extends string ? K : keyof K]: P extends keyof K
    ? K[P] extends (string | number)[] | undefined
      ? string[] | undefined
      : K[P] extends number[]
      ? string[]
      : K[P] extends number
      ? string
      : K[P] extends boolean | undefined
      ? 'true' | 'false' | undefined
      : K[P]
    : string
}

type SetSearch<K extends string | ValueParse> = (
  values: Values<K>,
  location?: LocationDescriptor<LocationState>
) => void

export const cleanSearch = (search: string) => search.replace(/\?/, '')
export const convertSearch = <K extends string | ValueParse>(
  search,
  values: Values<K>
) => {
  const oldSearch = cleanSearch(search)
  const params = new URLSearchParams(oldSearch)
  forEach({ ...values }, (value, key) => {
    if (typeof value !== 'object') params.set(key, String(value))
  })
  return params.toString()
}

export function useSearch<
  K extends string | ValueParse = keyof Parse<string>
>(): [Values<K>, SetSearch<K>] {
  const history = useHistory()
  const location = useLocation()
  const search = cleanSearch(location.search)
  const setSearch: SetSearch<K> = useCallback(
    (values, location) => {
      const newSearch = convertSearch(search, values)
      const newLocation =
        typeof location === 'string' ? { pathname: location } : location || {}
      if (search !== newSearch)
        history.push({ ...newLocation, search: newSearch })
    },
    [history, search]
  )

  const parse = useMemo(() => qsParse(search) as any, [search])
  return [parse, setSearch]
}

export const useDynamicStyleSheet = (styleSheet: string) => {
  const styleElementRef = useRef<HTMLStyleElement>()
  useEffect(() => {
    const styleElement = injectCss(styleSheet)
    return () => styleElement.remove()
  }, [styleSheet])
  return styleElementRef
}
